import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getStSupVisitForm,
  updateStSupVisitForm,
} from "../../../../../services/patientService";
import {
  FileCheckIcon,
  HeartPulseIcon,
  MedicalNotesIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  ST_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  COGNITIVE_LINGUISTIC_OPTIONS,
  COMMUNICATION_METHOD_OPTIONS,
  COMMUNICATION_STATUS_OPTIONS,
  COMMUNICATION_WITH_OPTIONS,
  COMPENSATORY_STRATEGY_OPTIONS,
  PARTICIPANT_OPTIONS,
  ST_SUP_ARRAY_KEYS,
  REQUIREMENTS_CHECKLIST_OPTIONS,
  SUPERVISORY_VISIT_TYPE_OPTIONS,
  SWALLOWING_STATUS_OPTIONS,
  VISIT_LOCATION_OPTIONS,
  YES_NO_OPTIONS,
  coerceExclusiveChoice,
  emptyStSupVisitPayload,
  toggleInList,
  today,
} from "./stSupVisitContent";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "../ptNotes/ptVisitCommon";
import StNotesCommonHeader from "./StNotesCommonHeader";
import {
  normalizeStCommonHeaderState,
  resolveStNotesCommonHeader,
  withNormalizedStCommonHeader,
} from "./stNotesCommonHeaderUtils";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyStSupVisitPayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const StSupVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyStSupVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickPtVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        commonHeader: normalizeStCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        supervisoryVisitType: coerceExclusiveChoice(
          loadedPayload.supervisoryVisitType ?? base.supervisoryVisitType,
        ),
        visitLocation: coerceExclusiveChoice(
          loadedPayload.visitLocation ?? base.visitLocation,
        ),
        communicationStatus: coerceExclusiveChoice(
          loadedPayload.communicationStatus ?? base.communicationStatus,
        ),
        swallowingStatus: coerceExclusiveChoice(
          loadedPayload.swallowingStatus ?? base.swallowingStatus,
        ),
        cognitiveLinguisticStatus: coerceExclusiveChoice(
          loadedPayload.cognitiveLinguisticStatus ??
            loadedPayload.cognitiveStatus ??
            base.cognitiveLinguisticStatus,
        ),
        compensatoryStrategies: coerceExclusiveChoice(
          loadedPayload.compensatoryStrategies ?? base.compensatoryStrategies,
        ),
        planOfCareReviewed: coerceExclusiveChoice(
          loadedPayload.planOfCareReviewed ?? base.planOfCareReviewed,
        ),
        followUpRequired: coerceExclusiveChoice(
          loadedPayload.followUpRequired ?? base.followUpRequired,
        ),
        planRemainsAppropriate: coerceExclusiveChoice(
          loadedPayload.planRemainsAppropriate ?? base.planRemainsAppropriate,
        ),
        communicationMethodOther:
          loadedPayload.communicationMethodOther ||
          loadedPayload.communicationOther ||
          "",
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      ST_SUP_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      // Migrate legacy nurse participant label → Supervising ST
      merged.participants = (merged.participants || []).map((p) =>
        p === "Supervising Nurse (RN/LPN)" ? "Supervising ST" : p,
      );

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveStNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Speech Therapy Supervisory Visit",
            formType: clinicalForm.formType || draft?.formType || "st_sup_visit",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...emptyStSupVisitPayload(), commonHeader },
          });
          return;
        }

        const res = await getStSupVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveStNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        ST_FORM_EDITOR_ROLES,
        "View only — only ST can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType:
      form?.formType || draft?.formType || clinicalForm?.formType || "st_sup_visit",
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      visitDate,
      ...stFields
    } = values;

    const nestedPayload = withNormalizedStCommonHeader({
      ...stFields,
      visitDate: visitDate ? formatDateForInput(visitDate) : "",
    });

    delete nestedPayload.clinicianLicense;
    delete nestedPayload.clinicianSignedDate;
    delete nestedPayload.patientRelationship;

    ST_SUP_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateStSupVisitForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.visitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showVisitTypeOther = values.supervisoryVisitType === "Other";
  const showLocationOther = values.visitLocation === "Other";
  const showParticipantsOther = (values.participants || []).includes(
    "Other Staff",
  );
  const showMethodOther = (values.communicationMethod || []).includes("Other");

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Speech Therapy (ST) Supervisory Visit Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home Health Agency — CMS Compliant Documentation
          </p>
          <p className="mt-0.5 text-[10px] text-slate-400">
            Per CMS Home Health Conditions of Participation (42 CFR §484.80).
            Supervisory Visit Requirements.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Visit Date"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Visit Date
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <StNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7. Visit Information */}
        <Section icon={MedicalNotesIcon} title="7. Visit Information">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <SubHeading>Type of Supervisory Visit (select one)</SubHeading>
              <ExclusiveChoiceGrid
                name="supervisoryVisitType"
                options={SUPERVISORY_VISIT_TYPE_OPTIONS}
                value={values.supervisoryVisitType || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("supervisoryVisitType", opt)}
                columns={1}
              />
              {showVisitTypeOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.supervisoryVisitTypeOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other visit type…"
                  onChange={(e) =>
                    setValue("supervisoryVisitTypeOther", e.target.value)
                  }
                />
              ) : null}
            </div>
            <div>
              <SubHeading>Location of Visit</SubHeading>
              <ExclusiveChoiceGrid
                name="visitLocation"
                options={VISIT_LOCATION_OPTIONS}
                value={values.visitLocation || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("visitLocation", opt)}
                columns={1}
              />
              {showLocationOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.visitLocationOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other location…"
                  onChange={(e) =>
                    setValue("visitLocationOther", e.target.value)
                  }
                />
              ) : null}
            </div>
            <div>
              <SubHeading>Participants</SubHeading>
              <CheckboxGrid
                options={PARTICIPANT_OPTIONS}
                values={values.participants}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("participants", opt)}
                columns={1}
              />
              {showParticipantsOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.participantsOther || ""}
                  disabled={!canEdit}
                  placeholder="Other staff…"
                  onChange={(e) =>
                    setValue("participantsOther", e.target.value)
                  }
                />
              ) : null}
            </div>
          </div>
        </Section>

        {/* 8. CMS Requirements */}
        <Section
          icon={ShieldCheckIcon}
          title="8. Supervisory Visit Requirements (CMS 42 CFR §484.80)"
        >
          <p className="text-[11px] italic text-slate-500 mb-2">
            The supervising therapist must perform an on-site visit and follow
            these requirements.
          </p>
          <CheckboxGrid
            options={REQUIREMENTS_CHECKLIST_OPTIONS}
            values={values.requirementsChecklist}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("requirementsChecklist", opt)}
            columns={2}
          />
        </Section>

        {/* 9. Clinical Assessment */}
        <Section icon={HeartPulseIcon} title="9. Clinical Assessment">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {[
              {
                title: "Communication Status",
                name: "communicationStatus",
                options: COMMUNICATION_STATUS_OPTIONS,
                value: values.communicationStatus,
                commentKey: "communicationComments",
                commentLabel: "Comments",
                commentValue: values.communicationComments,
              },
              {
                title: "Swallowing Status (if applicable)",
                name: "swallowingStatus",
                options: SWALLOWING_STATUS_OPTIONS,
                value: values.swallowingStatus,
                commentKey: "dietRestrictions",
                commentLabel: "Diet/Restrictions",
                commentValue: values.dietRestrictions,
              },
              {
                title: "Cognitive-Linguistic Status",
                name: "cognitiveLinguisticStatus",
                options: COGNITIVE_LINGUISTIC_OPTIONS,
                value: values.cognitiveLinguisticStatus,
                commentKey: "cognitiveComments",
                commentLabel: "Comments",
                commentValue: values.cognitiveComments,
              },
              {
                title: "Use of Compensatory Strategies",
                name: "compensatoryStrategies",
                options: COMPENSATORY_STRATEGY_OPTIONS,
                value: values.compensatoryStrategies,
                commentKey: "compensatoryNotes",
                commentLabel: "Examples/Notes",
                commentValue: values.compensatoryNotes,
              },
            ].map((card) => (
              <div
                key={card.name}
                className="flex h-full min-h-0 flex-col rounded-md border border-slate-200 bg-slate-50/40 p-3"
              >
                <SubHeading>{card.title}</SubHeading>
                <ExclusiveChoiceGrid
                  name={card.name}
                  options={card.options}
                  value={card.value || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue(card.name, opt)}
                  columns={3}
                />
                <div className="mt-2 flex-1">
                  <Field label={card.commentLabel}>
                    <textarea
                      rows={2}
                      className={`${textareaClass} h-[4.75rem] min-h-[4.75rem] resize-none`}
                      value={card.commentValue || ""}
                      disabled={!canEdit}
                      onChange={(e) => setValue(card.commentKey, e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* 10. Plan of Care Review */}
        <Section icon={FileCheckIcon} title="10. Plan of Care Review">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <SubHeading>Plan of Care Reviewed</SubHeading>
              <ExclusiveChoiceGrid
                name="planOfCareReviewed"
                options={YES_NO_OPTIONS}
                value={values.planOfCareReviewed || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("planOfCareReviewed", opt)}
                columns={2}
              />
            </div>
            <div>
              <SubHeading>Follow-up Required</SubHeading>
              <ExclusiveChoiceGrid
                name="followUpRequired"
                options={YES_NO_OPTIONS}
                value={values.followUpRequired || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("followUpRequired", opt)}
                columns={2}
              />
            </div>
            <div>
              <SubHeading>Plan of Care Remains Appropriate</SubHeading>
              <ExclusiveChoiceGrid
                name="planRemainsAppropriate"
                options={YES_NO_OPTIONS}
                value={values.planRemainsAppropriate || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("planRemainsAppropriate", opt)}
                columns={2}
              />
              <p className="mt-1 text-[10px] italic text-slate-400">
                (If No, see revisions below)
              </p>
            </div>
            <Field label="If Yes, specify">
              <input
                type="text"
                className={fieldClass}
                value={values.followUpSpecify || ""}
                disabled={!canEdit}
                placeholder="Specify follow-up…"
                onChange={(e) => setValue("followUpSpecify", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Revisions to Plan of Care / New Orders">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.planRevisions || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("planRevisions", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 11. Communication */}
        <Section icon={PhoneIcon} title="11. Communication">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="space-y-3">
              <div>
                <SubHeading>Communication with</SubHeading>
                <CheckboxGrid
                  options={COMMUNICATION_WITH_OPTIONS}
                  values={values.communicationWith}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("communicationWith", opt)}
                  columns={2}
                />
              </div>
              <div>
                <SubHeading>Method</SubHeading>
                <CheckboxGrid
                  options={COMMUNICATION_METHOD_OPTIONS}
                  values={values.communicationMethod}
                  canEdit={canEdit}
                  onToggle={(opt) =>
                    toggleListValue("communicationMethod", opt)
                  }
                  columns={2}
                />
                {showMethodOther ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.communicationMethodOther || ""}
                    disabled={!canEdit}
                    placeholder="Other method…"
                    onChange={(e) =>
                      setValue("communicationMethodOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
            </div>
            <Field label="Summary of Communication">
              <textarea
                rows={8}
                className={`${textareaClass} h-full min-h-[10.5rem]`}
                value={values.communicationSummary || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("communicationSummary", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Supervising Speech Therapist"
          patientSectionTitle="Patient/Caregiver (Acknowledgment of Visit)"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

StSupVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default StSupVisitForm;
